/**
 * Whether to offer passkey enrolment on this device.
 *
 * Deliberately per-device, not per-account -- unlike the display-name prompt, which records its
 * dismissal on the user's private-user-data doc so the decision follows them everywhere. A passkey
 * lives in *this* device's keychain, so someone who enrolled on their phone should still be offered
 * on their laptop. Storing the dismissal server-side would suppress exactly the prompt we want to
 * show.
 *
 * Keyed by uid as well as device, so a shared machine doesn't silently answer for the next person.
 */
const DISMISSED_KEY_PREFIX = 'passkeyPromptDismissed:';

export function hasDismissedPasskeyPrompt(uid: string | undefined): boolean {
    if (typeof window === 'undefined' || !uid) {
        return true; // never prompt without a signed-in user or a browser
    }
    try {
        return window.localStorage.getItem(`${DISMISSED_KEY_PREFIX}${uid}`) === '1';
    } catch {
        // Private browsing and some webviews throw on localStorage. Better to prompt than to
        // silently suppress a feature the user has never been offered.
        return false;
    }
}

/**
 * Records a deliberate dismissal -- enrolling, or choosing "Not now".
 *
 * Only called on an actual decision, never merely because the prompt rendered, so an offer the user
 * never got to (app backgrounded, navigation mid-render) comes back rather than being burned.
 */
export function rememberPasskeyPromptDismissed(uid: string | undefined): void {
    if (typeof window === 'undefined' || !uid) {
        return;
    }
    try {
        window.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${uid}`, '1');
    } catch {
        /* ignore quota / access errors -- costs at most a repeated prompt */
    }
}
